// Route-aware SEO head manager.
// Mutates document.title and the existing meta/link tags (description, keywords,
// canonical, Open Graph, Twitter Card) so metadata from a previous route never
// leaks into the next one.

import { SITE_NAME, absoluteUrl } from "./site";

export interface SeoInput {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  ogType?: "website" | "article";
  image?: string;
}

function setMeta(attr: "name" | "property", key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (!content) {
    if (existing) existing.remove();
    return;
  }

  let el = existing;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string): void {
  const selector = `link[rel="${rel}"]`;
  const existing = document.head.querySelector<HTMLLinkElement>(selector);

  if (!href) {
    if (existing) existing.remove();
    return;
  }

  let el = existing;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applySeo(input: SeoInput): void {
  const {
    title,
    description,
    keywords = "",
    path,
    ogType = "website",
    image = "",
  } = input;

  document.title = title;

  setMeta("name", "description", description);
  setMeta("name", "keywords", keywords);
  setLink("canonical", absoluteUrl(path));

  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:type", ogType);
  setMeta("property", "og:url", absoluteUrl(path));
  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:image", image);

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", image);
}